// ===================================
// APP.JS - FIXED ROUTING
// src/App.js
// Stage 11 Fix: Import ALL admin pages (no more placeholders)
// ===================================

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';

// Layout Components
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';

// Add after other admin page imports like AdminCoupons
import AdminFeedback from './pages/AdminFeedback';

// Add with public pages (near Login, Register)
import FeedbackForm from './pages/FeedbackForm';

// ========================================
// PUBLIC PAGES
// ========================================
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminLogin from './pages/AdminLogin';

// ========================================
// USER APP PAGES
// ========================================
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import BookSession from './pages/BookSession';
import StartSession from './pages/StartSession';
import Interview from './pages/Interview';
import Progress from './pages/Progress';
import PaymentSuccess from './pages/PaymentSuccess';
import UserSettings from './pages/UserSettings';
import Achievements from './pages/Achievements';
import NotFound from './pages/NotFound';
import MagicLogin from './pages/MagicLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';



// ========================================
// ADMIN PAGES - IMPORT ALL (NO PLACEHOLDERS!)
// ========================================
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminSessions from './pages/AdminSessions';
import AdminCategories from './pages/AdminCategories';
import AdminPrompts from './pages/AdminPrompts';
import AdminPrograms from './pages/AdminPrograms';
import AdminPricing from './pages/AdminPricing';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminSettings from './pages/AdminSettings';
import AdminSalesforce from './pages/AdminSalesforce';
import AdminVoiceSettings from './pages/AdminVoiceSettings';
import AdminErrors from './pages/AdminErrors';
import AdminCoupons from './pages/AdminCoupons';
import AdminCouponReports from './pages/AdminCouponReports';

// Interview redirect for legacy URLs (/interview/:id → /app/interview/:id)
const InterviewRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/interview/${id}`} replace />;
};

// Loading Spinner
const LoadingSpinner = () => (
  <div className="app-loading">
    <div className="loading-content">
      <div className="loading-logo">🎯</div>
      <div className="loading-spinner"></div>
      <p>Loading Career-Coach.AI...</p>
    </div>
  </div>
);

// Simple placeholder for truly future pages
const PlaceholderPage = ({ title, icon, description }) => (
  <div className="placeholder-page">
    <div className="placeholder-content">
      <span className="placeholder-icon">{icon}</span>
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Routes>
            {/* ========================================
                PUBLIC ROUTES (No auth required)
             ======================================== */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/magic-login" element={<MagicLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/feedback/:token" element={<FeedbackForm />} />

            {/* Legacy redirect: /interview/:id → /app/interview/:id */}
            <Route path="/interview/:id" element={<InterviewRedirect />} />
            
            {/* Legacy redirects for common paths without /app prefix */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/sessions" element={<Navigate to="/app/sessions" replace />} />
            <Route path="/book" element={<Navigate to="/app/book" replace />} />
            <Route path="/payment-success" element={<Navigate to="/app/payment-success" replace />} />
            <Route path="/start-session" element={<Navigate to="/app/start-session" replace />} />

            {/* ========================================
                USER APP ROUTES (Protected by AppLayout)
             ======================================== */}
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="sessions/:id" element={<SessionDetail />} />
              <Route path="book" element={<BookSession />} />
              <Route path="start-session" element={<StartSession />} />
              <Route path="progress" element={<Progress />} />
              <Route path="interview/:id" element={<Interview />} />
              <Route path="payment-success" element={<PaymentSuccess />} />
              <Route path="settings" element={<UserSettings />} />
              <Route path="achievements" element={<Achievements />} />
              
              {/* Future user pages */}
              <Route path="notifications" element={
                <PlaceholderPage 
                  icon="🔔" 
                  title="Notifications" 
                  description="View all your notifications" 
                />
              } />
              <Route path="help" element={
                <PlaceholderPage 
                  icon="❓" 
                  title="Help Center" 
                  description="Get help and support" 
                />
              } />
            </Route>

            {/* ========================================
                ADMIN ROUTES (Protected by AdminLayout)
                ALL pages are now properly imported!
             ======================================== */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="sessions" element={<AdminSessions />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="prompts" element={<AdminPrompts />} />
              <Route path="programs" element={<AdminPrograms />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="salesforce" element={<AdminSalesforce />} />
              <Route path="voice" element={<AdminVoiceSettings />} />
              <Route path="errors" element={<AdminErrors />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="coupon-reports" element={<AdminCouponReports />} />

            </Route>

            {/* ========================================
                FALLBACK - 404
             ======================================== */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;